import { useEffect, useMemo, useState } from 'react';
import {
  describeError,
  fetchIssueHours,
  type IssueHoursResponse,
  type TeamHoursRow,
} from '../api.js';
import {
  formatDate,
  formatHours,
  formatHoursTotal,
  formatPeriod,
  issuesPlural,
} from '../format.js';
import type { Period } from '../periods.js';

export interface DrilldownTarget {
  authorKey: string;
  displayName: string;
  /** Задан, когда открыли из ячейки: один сотрудник и одна команда. */
  team?: string;
  teamLabel?: string;
  /** Фильтр команд с экрана — когда открыли по фамилии, а не по ячейке. */
  fallbackTeams: string[];
}

interface Props {
  target: DrilldownTarget;
  period: Period;
  onClose: () => void;
}

/**
 * The tasks behind a number. Opened from a cell (employee × project) or from an
 * employee's name, and filtered exactly like the matrix — so the total here equals
 * the number that was clicked.
 */
export function IssueDrilldown({ target, period, onClose }: Props) {
  const [data, setData] = useState<IssueHoursResponse>();
  const [error, setError] = useState<string>();

  const teams = target.team === undefined ? target.fallbackTeams : [target.team];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setError(undefined);

    fetchIssueHours({ ...period, teams, authorKeys: [target.authorKey] }, controller.signal).then(
      setData,
      (cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      },
    );

    return () => controller.abort();
    // teams выводится из target, поэтому target его и покрывает.
  }, [target, period]);

  /**
   * Teams are the summary line: "these hours went to Marketing, on these projects".
   * Grouped here rather than in SQL — the API returns flat rows, and one place
   * doing the pivot keeps the numbers identical to the totals beside them.
   */
  const byTeam = useMemo(() => groupByTeam(data?.byTeam ?? []), [data]);

  const scope =
    target.team === undefined
      ? teams.length > 0
        ? `выбранные команды (${teams.length})`
        : 'все команды'
      : (target.teamLabel ?? (target.team === '' ? 'без команды' : target.team));

  return (
    <div className="overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="drawer" onClick={(event) => event.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <h2>{target.displayName}</h2>
            <div className="muted small">
              {scope} · {formatPeriod(period.from, period.to)}
            </div>
          </div>
          <button type="button" className="link" onClick={onClose}>
            закрыть
          </button>
        </div>

        {error && <div className="error">{error}</div>}
        {!data && !error && <div className="muted">Загрузка…</div>}

        {data && data.rows.length === 0 && <div className="muted">Задач с часами нет.</div>}

        {data && data.rows.length > 0 && (
          <>
            <div className="drawer-summary">
              <strong>{formatHoursTotal(data.totals.seconds)} ч</strong> в {data.totals.issues}{' '}
              {issuesPlural(data.totals.issues)}
              {data.truncated && (
                <span className="muted small">
                  {' '}
                  · показаны только первые по часам, список обрезан
                </span>
              )}
            </div>

            {byTeam.length > 0 && (
              <div className="team-summary">
                <div className="team-summary-head">По командам</div>
                {byTeam.map((team) => (
                  <div className="team-row" key={team.name ?? ''}>
                    <span
                      className={team.name ? 'team-name' : 'team-name muted'}
                      title={team.name ?? 'Поле Team в Jira не заполнено'}
                    >
                      {team.name ?? 'без команды'}
                    </span>
                    <span className="team-projects">
                      {team.projects.map((project) => (
                        <span className="team-project" key={project.projectId}>
                          <span className="option-key">{project.projectKey}</span>
                          {formatHours(project.seconds)}
                        </span>
                      ))}
                    </span>
                    {/* Decimal, like the project chips beside it: whole hours per team
                        drift visibly away from the total in the header. */}
                    <span className="team-hours">{formatHours(team.seconds)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="drawer-scroll">
              <table className="matrix">
                <thead>
                  <tr>
                    <th>Задача</th>
                    <th>Тема</th>
                    {target.team === undefined && <th>Команда</th>}
                    <th>Проект</th>
                    <th>Статус</th>
                    <th className="num">Записей</th>
                    <th className="num">Последняя</th>
                    <th className="num total-col">Часы</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.issueId}>
                      <td>
                        {row.url ? (
                          // Opens Jira in another tab: the dashboard stays where it was.
                          <a href={row.url} target="_blank" rel="noreferrer" className="issue-key">
                            {row.issueKey}
                          </a>
                        ) : (
                          <span className="issue-key">{row.issueKey}</span>
                        )}
                      </td>
                      <td className="issue-summary" title={row.summary}>
                        {row.summary}
                      </td>
                      {/* Колонка команды нужна, только когда открыли по фамилии:
                          из ячейки команда одна и повторялась бы в каждой строке. */}
                      {target.team === undefined && (
                        <td
                          className={row.team ? 'issue-team' : 'issue-team zero'}
                          title={row.team ?? undefined}
                        >
                          {row.team ?? '—'}
                        </td>
                      )}
                      <td title={row.projectName}>{row.projectKey}</td>
                      <td className="muted">{row.status ?? '—'}</td>
                      <td className="num muted">{row.entries}</td>
                      <td className="num muted">{formatDate(row.lastStartedAt)}</td>
                      <td className="num total-col">{formatHours(row.seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface TeamGroup {
  name: string | null;
  seconds: number;
  projects: TeamHoursRow[];
}

function groupByTeam(rows: TeamHoursRow[]): TeamGroup[] {
  const groups = new Map<string, TeamGroup>();

  for (const row of rows) {
    // An empty team is its own group, not a row to drop: without it the summary
    // would add up to less than the total.
    const key = row.team ?? '';
    const group = groups.get(key) ?? { name: row.team, seconds: 0, projects: [] };
    group.seconds += row.seconds;
    group.projects.push(row);
    groups.set(key, group);
  }

  return [...groups.values()].sort((a, b) => b.seconds - a.seconds);
}
